import React, { useState, useEffect } from "react";
import { Search, CheckCircle, XCircle, Star, MessageSquare, ShieldAlert, Eye, Trash2, X, Filter, TrendingUp, Users, Pencil } from "lucide-react";
import axiosInstance from "../../../axios";
import RenderRating from "../../Other/StarRating/RenderRating";
const avatarGradients = [
  "from-violet-500 to-purple-600",
  "from-blue-500 to-[#264b82]",
  "from-emerald-500 to-teal-600",
  "from-orange-500 to-amber-600",
  "from-pink-500 to-rose-600",
  "from-cyan-500 to-sky-600",
];
const getGradient = (name = "") => avatarGradients[name.charCodeAt(0) % avatarGradients.length];

const ReviewReplyNode = ({ reply, level = 0, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const grad = getGradient(reply.name);
  const hasChildren = reply.replies && reply.replies.length > 0;

  return (
    <div className={`space-y-2 ${level > 0 ? "ml-6 pl-4 border-l border-slate-200" : ""}`}>
      <div className="flex items-start gap-3 bg-slate-50/50 p-2.5 rounded-xl border border-slate-100 group relative">
        <div className={`h-6 w-6 bg-gradient-to-br ${grad} text-white rounded-lg flex items-center justify-center font-black text-[10px] uppercase shadow-xs shrink-0`}>
          {reply.name ? reply.name.charAt(0) : "U"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1">
            <span className="text-[11px] font-bold text-slate-800">{reply.name}</span>
            {reply.designation === "Admin" && (
              <span className="px-1.5 py-0.5 text-[8px] font-extrabold uppercase bg-indigo-50 border border-indigo-200 text-indigo-700 rounded">
                Admin
              </span>
            )}
            {reply.designation === "Instructor" && (
              <span className="px-1.5 py-0.5 text-[8px] font-extrabold uppercase bg-emerald-50 border border-emerald-200 text-emerald-700 rounded">
                Instructor
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-650 mt-0.5 whitespace-pre-line leading-relaxed">{reply.testimonial}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => onEdit(reply)}
            title="Edit reply"
            className="p-1 hover:bg-slate-100 text-slate-400 hover:text-indigo-650 rounded transition-colors cursor-pointer"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(reply.id)}
            title="Delete reply"
            className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded transition-colors cursor-pointer"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      {hasChildren && (
        <div className="pl-9">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50/50 hover:bg-indigo-50 text-indigo-600 hover:text-indigo-800 rounded-lg text-[10px] font-bold border border-indigo-100/50 transition-colors cursor-pointer"
          >
            <MessageSquare className="w-3 h-3 text-indigo-500" />
            {isExpanded ? "Hide replies" : `Show replies (${reply.replies.length})`}
          </button>
        </div>
      )}
      {hasChildren && isExpanded && reply.replies.map((r) => (
        <ReviewReplyNode key={r.id} reply={r} level={level + 1} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
};

const ReviewApprove = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedReview, setSelectedReview] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [adminReplyText, setAdminReplyText] = useState("");
  const [submittingAdminReply, setSubmittingAdminReply] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/testimonials");
      setReviews(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedReviewReplies = (parentId) => {
    const findReplies = (pId) => {
      const direct = reviews.filter(r => r.parentId === pId);
      return direct.map(r => ({
        ...r,
        replies: findReplies(r.id)
      }));
    };
    return findReplies(parentId);
  };

  const handleAdminReplySubmit = async (e) => {
    e.preventDefault();
    if (!adminReplyText.trim() || !selectedReview) return;
    try {
      setSubmittingAdminReply(true);
      const res = await axiosInstance.post(`/testimonials/${selectedReview.id}/reply`, {
        comment: adminReplyText
      });
      if (res.data && !res.data.error) {
        setAdminReplyText("");
        await fetchReviews();
      } else {
        alert(res.data?.message || "Failed to submit reply.");
      }
    } catch (error) {
      console.error("Failed to submit admin reply:", error);
      alert(error.response?.data?.message || "Error submitting reply.");
    } finally {
      setSubmittingAdminReply(false);
    }
  };

  const handleStatusUpdate = async (review, newStatus) => {
    setActionLoadingId(review.id);
    try {
      await axiosInstance.put(`/testimonials/${review.id}`, {
        ...review,
        status: newStatus,
      });
      await fetchReviews();
      if (selectedReview && selectedReview.id === review.id) {
        setSelectedReview({ ...review, status: newStatus });
      }
    } catch (error) {
      console.error("Failed to update review status:", error);
      alert("Error updating review status. Please try again.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteReview = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDeleteReview = async () => {
    if (!deleteConfirmId) return;
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    setActionLoadingId(id);
    try {
      await axiosInstance.delete(`/testimonials/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      if (selectedReview && selectedReview.id === id) setSelectedReview(null);
    } catch (error) {
      console.error("Failed to delete review:", error);
      alert("Error deleting review. Please try again.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleEditChange = (field, value) => {
    setEditingReview((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingReview?.name?.trim() || !editingReview?.testimonial?.trim()) {
      alert("Name and Testimonial/Message are required.");
      return;
    }
    setActionLoadingId(editingReview.id);
    try {
      await axiosInstance.put(`/testimonials/${editingReview.id}`, {
        name: editingReview.name,
        designation: editingReview.designation,
        company: editingReview.company || "",
        rating: editingReview.parentId ? null : (editingReview.rating || "5"),
        testimonial: editingReview.testimonial,
        status: editingReview.status !== undefined ? editingReview.status : 1,
      });
      setEditingReview(null);
      await fetchReviews();
    } catch (error) {
      console.error("Failed to update review:", error);
      alert("Error saving review changes. Please try again.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredReviews = reviews.filter((review) => {
    if (review.parentId) return false;

    const matchesSearch =
      review.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.testimonial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (review.company || review.designation || "Course")?.toLowerCase().includes(searchTerm.toLowerCase());

    const isApproved = review.status === 1 || review.status === "1";
    if (activeTab === "approved") return matchesSearch && isApproved;
    if (activeTab === "pending") return matchesSearch && !isApproved;
    return matchesSearch;
  });

  const approvedCount = reviews.filter((r) => !r.parentId && (r.status === 1 || r.status === "1")).length;
  const pendingCount = reviews.filter((r) => !r.parentId && (r.status === 0 || r.status === "0")).length;
  const parentReviewsCount = reviews.filter((r) => !r.parentId).length;
  const approvalRate = parentReviewsCount > 0 ? Math.round((approvedCount / parentReviewsCount) * 100) : 0;

  const tabs = [
    { id: "all", label: "All Reviews", count: parentReviewsCount },
    { id: "approved", label: "Approved", count: approvedCount },
    { id: "pending", label: "Pending", count: pendingCount },
  ];

  return (
    <div className="min-h-screen bg-slate-50/40">
      <div className="max-w-[1400px] mx-auto px-6 py-8">

        {/* ── Page Header ── */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="h-12 w-12 bg-gradient-to-br from-[#264b82] to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-amber-400 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[8px] font-black text-white">{pendingCount}</span>
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-black text-slate-800 tracking-tight">Reviews Moderation</h1>
                <p className="text-sm text-slate-500 mt-0.5">Manage and publish student testimonials</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-xs">
              <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-xs font-bold text-slate-600">Live moderation panel</span>
            </div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          {/* Approved */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-200 hover:shadow-sm transition-all duration-300 flex items-center gap-4">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100 shrink-0">
              <CheckCircle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Approved Reviews</p>
              <p className="text-xl font-black text-slate-800 mt-0.5">{approvedCount}</p>
            </div>
            <span className="text-[9px] font-extrabold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0">Live</span>
          </div>

          {/* Pending */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-amber-200 hover:shadow-sm transition-all duration-300 flex items-center gap-4">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl border border-amber-100 shrink-0">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Awaiting Moderation</p>
              <p className="text-xl font-black text-slate-800 mt-0.5">{pendingCount}</p>
            </div>
            {pendingCount > 0 && (
              <span className="text-[9px] font-extrabold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0 animate-pulse">Pending</span>
            )}
          </div>

          {/* Approval Rate */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-indigo-200 hover:shadow-sm transition-all duration-300 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-100 shrink-0">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Approval Rate</p>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <p className="text-xl font-black text-slate-800">{approvalRate}%</p>
                <p className="text-[10px] text-slate-400 font-bold">({reviews.length} Total)</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 mb-5 flex flex-col sm:flex-row gap-3 items-center justify-between shadow-xs">
          {/* Search */}
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, course, or content…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 focus:bg-white transition-all"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded-md transition-colors cursor-pointer">
                <X className="h-3.5 w-3.5 text-slate-400" />
              </button>
            )}
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-white text-slate-800 shadow-sm border border-slate-200/80"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
                <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-md ${
                  activeTab === tab.id ? "bg-slate-800 text-white" : "bg-slate-200/60 text-slate-500"
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Main Table Card ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          {loading ? (
            <div className="py-28 flex flex-col items-center gap-5">
              <div className="relative">
                <div className="h-14 w-14 rounded-full border-4 border-slate-100 border-t-[#264b82] animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-slate-300" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-slate-600">Loading testimonials</p>
                <p className="text-xs text-slate-400 mt-1">Fetching latest reviews…</p>
              </div>
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="py-28 flex flex-col items-center gap-4">
              <div className="h-20 w-20 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200 flex items-center justify-center">
                <MessageSquare className="h-8 w-8 text-slate-300" />
              </div>
              <div className="text-center">
                <p className="text-base font-bold text-slate-600">No reviews found</p>
                <p className="text-sm text-slate-400 mt-1">
                  {searchTerm ? "Try adjusting your search terms" : "No testimonials in this category yet"}
                </p>
              </div>
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline underline-offset-2 cursor-pointer">
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-4 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest bg-slate-50/60">Student</th>
                    <th className="px-6 py-4 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest bg-slate-50/60">Course</th>
                    <th className="px-6 py-4 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest bg-slate-50/60">Rating</th>
                    <th className="px-6 py-4 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest bg-slate-50/60">Review</th>
                    <th className="px-6 py-4 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest bg-slate-50/60">Status</th>
                    <th className="px-6 py-4 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest bg-slate-50/60 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReviews.map((review, idx) => {
                    const isApproved = review.status === 1 || review.status === "1";
                    const isActionLoading = actionLoadingId === review.id;
                    const grad = getGradient(review.name);

                    return (
                      <tr
                        key={review.id}
                        className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors group"
                      >
                        {/* Student */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 bg-gradient-to-br ${grad} text-white rounded-xl flex items-center justify-center font-black text-sm shadow-sm shrink-0 uppercase`}>
                              {review.name ? review.name.charAt(0) : "S"}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800 leading-tight">{review.name}</p>
                              <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Student</p>
                            </div>
                          </div>
                        </td>

                        {/* Course */}
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 max-w-[150px] truncate">
                            {review.company || review.designation || "yslearn Course"}
                          </span>
                        </td>

                        {/* Rating */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <RenderRating ratings={review.rating || "5"} manageVotes={false} />
                        </td>

                        {/* Review */}
                        <td className="px-6 py-4 max-w-xs">
                          <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed">{review.testimonial}</p>
                          {review.testimonial?.length > 80 && (
                            <button
                              onClick={() => setSelectedReview(review)}
                              className="mt-1 text-[11px] font-bold text-indigo-500 hover:text-indigo-700 cursor-pointer transition-colors"
                            >
                              Read more →
                            </button>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isApproved ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/80">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              Published
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200/80">
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              Pending
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {/* View */}
                            <button
                              onClick={() => setSelectedReview(review)}
                              title="View details"
                              className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>

                            {/* Edit */}
                            <button
                              onClick={() => setEditingReview(review)}
                              title="Edit review"
                              className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 transition-all cursor-pointer"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>

                            {/* Approve */}
                            <button
                              onClick={() => handleStatusUpdate(review, 1)}
                              disabled={isApproved || isActionLoading}
                              title="Approve"
                              className={`h-8 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer border ${
                                isApproved
                                  ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                                  : "bg-[#264b82] text-white border-[#264b82] hover:bg-[#1d3a68] shadow-sm hover:shadow-md hover:shadow-blue-100 active:scale-95"
                              }`}
                            >
                              {isActionLoading ? "…" : "Approve"}
                            </button>

                            {/* Reject */}
                            <button
                              onClick={() => handleStatusUpdate(review, 0)}
                              disabled={!isApproved || isActionLoading}
                              title="Hold / Unpublish"
                              className={`h-8 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer border ${
                                !isApproved
                                  ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                                  : "bg-slate-700 text-white border-slate-700 hover:bg-slate-800 shadow-sm active:scale-95"
                              }`}
                            >
                              {isActionLoading ? "…" : "Hold"}
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDeleteReview(review.id)}
                              disabled={isActionLoading}
                              title="Delete permanently"
                              className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100 transition-all cursor-pointer"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Table Footer */}
              <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
                <p className="text-xs text-slate-400 font-semibold">
                  Showing <span className="font-bold text-slate-600">{filteredReviews.length}</span> of <span className="font-bold text-slate-600">{reviews.length}</span> reviews
                </p>
                <div className="flex items-center gap-1.5">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Auto-refreshed</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Review Detail Modal ── */}
      {selectedReview && (() => {
        const replies = getSelectedReviewReplies(selectedReview.id);
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setSelectedReview(null)}
          >
            <div
              className="w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 modal-enter flex flex-col max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient Top Bar */}
              <div className="h-1 bg-gradient-to-r from-indigo-400 via-[#264b82] to-violet-500" />

              {/* Modal Header */}
              <div className="px-7 pt-6 pb-5 border-b border-slate-100 flex items-center justify-between shrink-0">
                <div>
                  <p className="text-[10px] font-extrabold text-indigo-500 uppercase tracking-widest mb-1">Testimonial Detail</p>
                  <h3 className="text-lg font-black text-slate-800">Review Sheet & Discussion</h3>
                </div>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="h-9 w-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Modal Body Container (Scrollable) */}
              <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
                {/* Author Block */}
                <div className="flex items-center gap-4 bg-slate-50/50 border border-slate-150 rounded-2xl p-4">
                  <div className={`h-12 w-12 bg-gradient-to-br ${getGradient(selectedReview.name)} text-white rounded-xl flex items-center justify-center font-black text-xl shadow-sm uppercase`}>
                    {selectedReview.name ? selectedReview.name.charAt(0) : "S"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-black text-slate-800">{selectedReview.name}</h4>
                    <p className="text-xs text-slate-500 font-semibold mt-0.5">{selectedReview.company || selectedReview.designation || "yslearn Course"}</p>
                    <div className="mt-1.5">
                      <RenderRating ratings={selectedReview.rating || "5"} manageVotes={false} />
                    </div>
                  </div>
                  <div>
                    {selectedReview.status === 1 || selectedReview.status === "1" ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-250">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-250">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        Pending
                      </span>
                    )}
                  </div>
                </div>

                {/* Review Text */}
                <div className="space-y-2">
                  <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Review Message</p>
                  <div className="relative bg-gradient-to-br from-indigo-50/20 to-slate-50 rounded-2xl p-4 border border-indigo-100/50">
                    <span className="absolute top-2 left-3 text-4xl text-indigo-100 font-serif leading-none select-none">"</span>
                    <p className="text-slate-700 text-sm leading-relaxed font-medium relative z-10 pt-2 whitespace-pre-line">
                      {selectedReview.testimonial}
                    </p>
                  </div>
                </div>

                {/* Discussion / Replies Section */}
                <div className="space-y-4 border-t border-slate-100 pt-4">
                  <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Discussion Thread ({replies.length})</p>
                  
                  {replies.length === 0 ? (
                    <p className="text-xs text-slate-400 font-medium italic">No comments or replies on this review yet.</p>
                  ) : (
                    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-1">
                      {replies.map((reply) => (
                        <ReviewReplyNode
                          key={reply.id}
                          reply={reply}
                          onEdit={setEditingReview}
                          onDelete={handleDeleteReview}
                        />
                      ))}
                    </div>
                  )}

                  {/* Add Admin Reply Form */}
                  <form onSubmit={handleAdminReplySubmit} className="space-y-2 mt-4 bg-slate-50 p-3 rounded-2xl border border-slate-150">
                    <textarea
                      value={adminReplyText}
                      onChange={(e) => setAdminReplyText(e.target.value)}
                      placeholder="Type reply or comment as Admin..."
                      rows="2"
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-semibold text-slate-750 focus:outline-none focus:ring-2 focus:ring-slate-100 transition-all resize-none"
                      required
                    />
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={submittingAdminReply}
                        className="px-4 py-1.5 bg-slate-900 hover:bg-black text-white rounded-lg text-[10px] font-bold shadow-xs transition-all cursor-pointer"
                      >
                        {submittingAdminReply ? "Sending..." : "Post Comment"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Modal Footer Actions */}
              <div className="px-7 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setSelectedReview(null)}
                  className="h-9 px-4 bg-white hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer border border-slate-200/80"
                >
                  Close
                </button>

                {/* Edit */}
                <button
                  type="button"
                  onClick={() => {
                    setEditingReview(selectedReview);
                    setSelectedReview(null);
                  }}
                  className="h-9 px-4 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-xs active:scale-95 bg-white"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>

                {selectedReview.status === 1 || selectedReview.status === "1" ? (
                  <button
                    type="button"
                    onClick={() => handleStatusUpdate(selectedReview, 0)}
                    className="h-9 px-4 bg-slate-700 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-xs active:scale-95"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Unpublish
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleStatusUpdate(selectedReview, 1)}
                    className="h-9 px-4 bg-[#264b82] hover:bg-[#1d3a68] text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-xs shadow-blue-200 active:scale-95"
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    Approve
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Edit Review Modal ── */}
      {editingReview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setEditingReview(null)}
        >
          <form
            onSubmit={handleSaveEdit}
            className="w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 modal-enter"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Gradient Top Bar */}
            <div className="h-1 bg-gradient-to-r from-amber-400 via-[#264b82] to-indigo-500" />

            {/* Modal Header */}
            <div className="px-7 pt-6 pb-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-extrabold text-amber-500 uppercase tracking-widest mb-1">Moderator Tool</p>
                <h3 className="text-lg font-black text-slate-800">
                  {editingReview.parentId ? "Edit Reply" : "Edit Testimonial"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setEditingReview(null)}
                className="h-9 w-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 border border-transparent hover:border-slate-200 transition-all cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Form Fields Container */}
            <div className="px-7 py-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Name */}
              <div>
                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">
                  {editingReview.parentId ? "Replier Name" : "Student Name"}
                </label>
                <input
                  type="text"
                  required
                  value={editingReview.name || ""}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                  placeholder="e.g. John Doe"
                />
              </div>

              {/* Course / Company */}
              {!editingReview.parentId && (
                <div>
                  <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Course / Company</label>
                  <input
                    type="text"
                    value={editingReview.company || ""}
                    onChange={(e) => handleEditChange("company", e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                    placeholder="e.g. Generative AI - Introduction"
                  />
                </div>
              )}

              {/* Designation */}
              <div>
                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Designation</label>
                <input
                  type="text"
                  value={editingReview.designation || ""}
                  onChange={(e) => handleEditChange("designation", e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                  placeholder="e.g. Student / Software Engineer"
                />
              </div>

              {/* Rating */}
              {!editingReview.parentId && (
                <div>
                  <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Rating</label>
                  <select
                    value={editingReview.rating || "5"}
                    onChange={(e) => handleEditChange("rating", e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                  >
                    <option value="5">5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="2">2 Stars</option>
                    <option value="1">1 Star</option>
                  </select>
                </div>
              )}

              {/* Testimonial Message */}
              <div>
                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">
                  {editingReview.parentId ? "Reply Message" : "Testimonial Text"}
                </label>
                <textarea
                  required
                  rows={5}
                  value={editingReview.testimonial || ""}
                  onChange={(e) => handleEditChange("testimonial", e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all resize-none"
                  placeholder={editingReview.parentId ? "Write the reply message..." : "Write the testimonial message..."}
                />
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="px-7 pb-7 border-t border-slate-100 pt-5 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setEditingReview(null)}
                className="h-10 px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-all cursor-pointer border border-slate-200/80"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={actionLoadingId === editingReview.id}
                className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-emerald-200 active:scale-95 disabled:opacity-55"
              >
                {actionLoadingId === editingReview.id ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteConfirmId && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setDeleteConfirmId(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 modal-enter p-6 flex flex-col items-center text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Icon */}
            <div className="h-12 w-12 bg-red-50 text-red-500 border border-red-100 rounded-2xl flex items-center justify-center mb-4">
              <Trash2 className="h-5 w-5" />
            </div>

            <h3 className="text-base font-black text-slate-800 tracking-tight">Delete Review</h3>
            <p className="text-xs text-slate-500 mt-2 font-semibold leading-relaxed">
              Are you sure you want to delete this review? This action cannot be undone.
            </p>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 mt-6 w-full">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-[11px] font-extrabold transition-all cursor-pointer border border-slate-200/50 active:scale-95"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteReview}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-[11px] font-extrabold transition-all cursor-pointer shadow-sm active:scale-95 flex items-center justify-center gap-1.5"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
        .modal-enter { animation: modal-in 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
};

export default ReviewApprove;
